import { useMemo } from 'react'

import campaignsJson from '../../demo/fixtures/campaigns.json'
import chartsJson from '../../demo/fixtures/w5/charts.json'
import benchmarkJson from '../../demo/fixtures/w5/benchmark.json'
import copyJson from '../../demo/fixtures/w5/copy.json'
import metricsJson from '../../demo/fixtures/w5/metrics.json'
import reportJson from '../../demo/fixtures/w5/report.json'
import rulesJson from '../../demo/fixtures/w5/rules.json'
import tasksJson from '../../demo/fixtures/w5/tasks.json'
import engineRulesJson from '../../demo/fixtures/w3/rules.json'
import { formatInteger, formatUsd } from '../../demo/format'
import { cpaTargets } from '../../demo/today'
import { stationNow, useDemoStore, type W5CustomRule, type W5State, type W5TriggerEntry } from '../../demo/store'
import type { Ad, Campaign, Product } from '../../demo/types'

type BadgeTone = 'green' | 'red' | 'orange' | 'gray' | 'blue'
type ValueTone = 'bad' | 'warn' | 'ok' | 'off'

interface CampaignFixture {
  meta: { today: string; syncedAt: string }
  products: Product[]
  campaigns: Campaign[]
}

interface MetricSeed {
  key: string
  label: string
  value: string
  delta: string
  deltaDir: string
  good: boolean
  status: string
  note: string
}

interface MonitorRuleSeed {
  key: string
  on: boolean
  cond: string
  window: string
  level: string
  active: number
}

interface AlertSeed {
  ruleKey: string
  campaignId: string
  adId?: string
  tone: 'bad' | 'warn' | 'ok'
  level: string
  object: string
  message: string
  rule: string
  owner: 'ai' | 'you' | 'info'
  action: string
}

interface MetricsFixture {
  days: string[]
  items: MetricSeed[]
  rules: MonitorRuleSeed[]
  alerts: AlertSeed[]
}

interface FindingSeed {
  id: string
  campaignId: string
  tone: 'bad' | 'warn' | 'ok'
  title: string
  evidence: string
  why: string
  impact: string
  todo: string
  owner: 'ai' | 'you'
  state: string
  handoff: { willDo: string; wontDo: string }
}

interface ReportFixture {
  title: string
  issue: string
  range: string
  made: string
  grade: string
  pages: number
  recipients: Array<{ name: string; email: string }>
  lead: string
  stats: Array<{ value: string; label: string; tone?: string }>
  kpis: Array<{ label: string; value: string; delta: string; good: boolean }>
  findings: FindingSeed[]
  actions: Array<{ owner: 'ai' | 'you'; text: string; when: string }>
  history: Array<{ range: string; issue: string; grade: string }>
  schedule: string
  foot: string
  evidence: Array<{ value: string; sub: string; chart: 'cpa' | 'freq' | null }>
}

interface AutoRuleSeed {
  key: string
  name: string
  purpose: string
  cond: string
  action: string
  scope: string
  weekRuns: number
  needsConfirm: boolean
  locked: boolean
  on: boolean
}

interface RulesFixture {
  items: AutoRuleSeed[]
  groupOrder: string[]
  guards: string[]
  pending: { rule: string; obj: string; what: string; why: string[]; at: string; note: string }
  log: W5TriggerEntry[]
  logState: Record<string, { label: string; tone: string }>
}

interface BenchmarkFixture {
  source: string
  rows: Array<{ metric: string; you: string; median: string; top25: string; position: string; pct: number }>
  trend: { title: string; you: number[]; mkt: number[]; lo: number; hi: number }
  trendNote: string
  signals: Array<{ icon: string; signal: string; action: string; state: string; label: string }>
  signalState: Record<string, string>
}

interface ChartSeed {
  title: string
  unit: string
  vals: number[]
  threshold: number
  thr_label: string
  base: number | null
  base_label: string
  lo: number
  hi: number
  ticks: number[]
}

const fixture = campaignsJson as unknown as CampaignFixture
const metrics = metricsJson as unknown as MetricsFixture
const report = reportJson as unknown as ReportFixture
const rules = rulesJson as unknown as RulesFixture
const benchmark = benchmarkJson as unknown as BenchmarkFixture
const charts = chartsJson as unknown as { cpa: ChartSeed; freq: ChartSeed }
const productsById = new Map(fixture.products.map((product) => [product.id, product]))
const overFactor = (engineRulesJson as { cpaOverFactor: number }).cpaOverFactor

function sum(items: number[]): number {
  return items.reduce((total, value) => total + value, 0)
}

function compact(value: number): string {
  return value <= 0 ? '—' : `${(value / 10_000).toFixed(1)} 万`
}

function money(value: number | null, decimals: 0 | 2 = 2): string {
  return value === null || !Number.isFinite(value) ? '—' : formatUsd(value, decimals)
}

function ratio(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null
}

function productOf(campaign: Campaign): Product | undefined {
  return productsById.get(campaign.productId)
}

function statusDisplay(campaign: Campaign): { label: string; tone: BadgeTone } {
  if (campaign.status === 'running') return { label: '运行中', tone: 'green' }
  if (campaign.status === 'attention') return { label: '需要处理', tone: 'red' }
  if (campaign.status === 'paused') return { label: '已暂停', tone: 'orange' }
  if (campaign.status === 'pending') return { label: '待确认', tone: 'blue' }
  return { label: '已完成', tone: 'gray' }
}

function dateAfter(days: number): string {
  const date = new Date(`${fixture.meta.today}T00:00:00Z`)
  date.setUTCDate(date.getUTCDate() + days)
  return `${date.getUTCMonth() + 1}月${date.getUTCDate()}日`
}

export function normalizeW5(w5: W5State): W5State {
  return {
    ...w5,
    proposal: { ...w5.proposal, fromDailyMax: w5.proposal.fromDailyMax ?? null },
    reportExports: w5.reportExports ?? [],
    reportSendCount: w5.reportSendCount ?? 0,
    nextTriggerSeq: w5.nextTriggerSeq ?? w5.triggerLog.length + 1,
  }
}

export function parseStationTime(value: string): [number, number, number, number] {
  const match = value.match(/^(\d{1,2})月(\d{1,2})日\s+(\d{2}):(\d{2})$/)
  return match ? [Number(match[1]), Number(match[2]), Number(match[3]), Number(match[4])] : [0, 0, 0, 0]
}

export function fillTemplate(template: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce((result, [key, value]) => result.replaceAll(`{${key}}`, String(value)), template)
}

type RuleComparable = Pick<AutoRuleSeed | W5CustomRule, 'purpose' | 'cond' | 'action' | 'scope'>

export function findDuplicateRule(allRules: Array<AutoRuleSeed | W5CustomRule>, candidate: RuleComparable) {
  return allRules.find((rule) => rule.purpose === candidate.purpose
    && rule.cond === candidate.cond
    && rule.action === candidate.action
    && rule.scope === candidate.scope)
}

export interface LiveAccount {
  spend: number
  orders: number
  revenue: number
  reach: number
  impressions: number
  clicks: number
  cpa: number
  roas: number
  ctr: number
  cpm: number
  frequency: number
}

export interface LiveTaskRow {
  campaignId: string
  image: string
  adName: string
  product: string
  status: string
  statusTone: BadgeTone
  spend: string
  ctr: string
  costLabel: string
  costValue: string
  costTone: ValueTone
  roas: string
  reach: string
  reachTone: ValueTone
  monitorTone: ValueTone
  monitorText: string
}

export function selectAccount(campaigns: Campaign[], ads: Ad[]): LiveAccount {
  const spend = sum(campaigns.map((campaign) => campaign.spendMonth))
  const orders = sum(campaigns.map((campaign) => campaign.orders))
  const revenue = sum(campaigns.map((campaign) => campaign.revenue))
  const reach = sum(campaigns.map((campaign) => campaign.reach))
  const impressions = sum(campaigns.map((campaign) => campaign.impressions))
  const clicks = sum(ads.map((ad) => ad.clicks))
  return {
    spend,
    orders,
    revenue,
    reach,
    impressions,
    clicks,
    cpa: ratio(spend, orders) ?? 0,
    roas: ratio(revenue, spend) ?? 0,
    ctr: ratio(clicks, sum(ads.map((ad) => ad.impressions))) ?? 0,
    cpm: (ratio(spend, reach) ?? 0) * 1000,
    frequency: ratio(impressions, reach) ?? 0,
  }
}

function alertObject(seed: AlertSeed, campaigns: Campaign[], ads: Ad[]): string {
  const campaign = campaigns.find((item) => item.id === seed.campaignId)
  if (seed.adId) return ads.find((ad) => ad.id === seed.adId)?.name ?? seed.object
  return campaign?.adName ?? seed.object
}

function liveAlertMessage(seed: AlertSeed, campaigns: Campaign[], ads: Ad[], account: LiveAccount): string {
  const campaign = campaigns.find((item) => item.id === seed.campaignId)
  if (!campaign) return seed.message
  if (seed.ruleKey === 'M1') {
    const cpa = ratio(campaign.spendMonth, campaign.orders) ?? 0
    const target = cpaTargets[campaign.id] ?? 0
    const over = target > 0 ? Math.round((cpa / target - 1) * 100) : 0
    return `每单成本 ${money(cpa)}，高于目标 ${money(target, 0)} ${over}%，连续 3 天`
  }
  if (seed.ruleKey === 'M2' && seed.adId) {
    const ad = ads.find((item) => item.id === seed.adId)
    const ctr = ad ? ratio(ad.clicks, ad.impressions) ?? 0 : 0
    const below = account.ctr > 0 ? Math.round((1 - ctr / account.ctr) * 100) : 0
    return `点击率 ${(ctr * 100).toFixed(2)}%，低于账户基准 ${below}%`
  }
  if (seed.ruleKey === 'M3') return '投放期内频次 3.4，超过阈值 3.0，重复触达上升'
  if (seed.ruleKey === 'M5') return `投放完成，广告回报 ${(campaign.metrics.roas ?? 0).toFixed(1)} 为账户最佳`
  return seed.message
}

export function selectAlerts(campaigns: Campaign[], ads: Ad[], w5: W5State) {
  const account = selectAccount(campaigns, ads)
  return metrics.alerts
    .filter((seed) => w5.monitorRules[seed.ruleKey] !== false)
    .filter((seed) => seed.ruleKey !== 'M6' || campaigns.some((campaign) => campaign.id === seed.campaignId && campaign.attentionReason === 'product_link_invalid'))
    .map((seed) => ({
      ...seed,
      object: alertObject(seed, campaigns, ads),
      message: liveAlertMessage(seed, campaigns, ads, account),
    }))
}

export function selectTaskRows(campaigns: Campaign[], ads: Ad[], w5: W5State): LiveTaskRow[] {
  const liveAlerts = selectAlerts(campaigns, ads, w5)
  return campaigns.map((campaign) => {
    const product = productOf(campaign)
    const campaignAds = ads.filter((ad) => ad.campaignId === campaign.id)
    const taskCtr = ratio(sum(campaignAds.map((ad) => ad.clicks)), sum(campaignAds.map((ad) => ad.impressions)))
    const isSales = campaign.objective === 'sales'
    const cost = isSales ? ratio(campaign.spendMonth, campaign.orders) : (ratio(campaign.spendMonth, campaign.reach) ?? 0) * 1000
    const target = cpaTargets[campaign.id]
    const alertCount = liveAlerts.filter((alert) => alert.campaignId === campaign.id && alert.tone !== 'ok').length
    const status = statusDisplay(campaign)
    let monitorText = alertCount ? `${alertCount} 项告警` : '正常'
    let monitorTone: ValueTone = alertCount ? 'bad' : 'ok'
    if (campaign.attentionReason === 'product_link_invalid') { monitorText = '链接失效 · 停投'; monitorTone = 'bad' }
    else if (campaign.spendMonth === 0 && campaign.status !== 'completed') { monitorText = '数据积累中'; monitorTone = 'off' }
    else if (campaign.status === 'completed') { monitorText = '已结束'; monitorTone = 'off' }
    return {
      campaignId: campaign.id,
      image: product?.image ?? '',
      adName: campaign.adName,
      product: product?.name ?? campaign.adName,
      status: status.label,
      statusTone: status.tone,
      spend: campaign.spendMonth > 0 ? formatUsd(campaign.spendMonth, 0) : '—',
      ctr: taskCtr === null ? '—' : `${(taskCtr * 100).toFixed(2)}%`,
      costLabel: isSales ? '每单' : '千次',
      costValue: campaign.spendMonth > 0 ? money(cost) : '—',
      costTone: campaign.spendMonth <= 0 ? 'off' : target && cost !== null && cost > target * overFactor ? 'bad' : 'ok',
      roas: isSales && campaign.metrics.roas !== null ? campaign.metrics.roas.toFixed(1) : '—',
      reach: compact(campaign.reach),
      reachTone: campaign.reach <= 0 ? 'off' : campaign.objective === 'awareness' && campaign.result.target > 0 && campaign.result.current / campaign.result.target >= 0.8 ? 'warn' : 'ok',
      monitorTone,
      monitorText,
    }
  })
}

function metricValue(key: string, account: LiveAccount): string {
  if (key === 'spend') return formatUsd(account.spend, 0)
  if (key === 'orders') return formatInteger(account.orders)
  if (key === 'cpa') return money(account.cpa)
  if (key === 'roas') return account.roas.toFixed(1)
  if (key === 'ctr') return `${(account.ctr * 100).toFixed(2)}%`
  if (key === 'cpm') return money(account.cpm)
  if (key === 'reach') return compact(account.reach)
  return compact(account.impressions)
}

function metricNote(item: MetricSeed, campaigns: Campaign[]): string {
  if (item.note === '{n} 个任务') return `${campaigns.length} 个任务`
  if (item.note === '{n} 个任务超目标') {
    const count = campaigns.filter((campaign) => {
      const target = cpaTargets[campaign.id]
      return target && campaign.orders > 0 && campaign.spendMonth / campaign.orders > target * overFactor
    }).length
    return `${count} 个任务超目标`
  }
  if (item.note === '{n} 个任务人群近饱和') {
    const count = campaigns.filter((campaign) => campaign.objective === 'awareness' && campaign.result.target > 0 && campaign.result.current / campaign.result.target >= 0.8).length
    return `${count} 个任务人群近饱和`
  }
  return item.note
}

function liveCharts(campaigns: Campaign[]) {
  const mug = campaigns.find((campaign) => campaign.id === 'c-mug-us')
  const product = mug ? productOf(mug)?.name ?? '轻量保温杯' : '轻量保温杯'
  const target = cpaTargets['c-mug-us'] ?? 38
  const cpa = mug && mug.orders > 0 ? mug.spendMonth / mug.orders : charts.cpa.vals.at(-1) ?? target
  return {
    cpa: {
      ...charts.cpa,
      title: `${product} · 每单成本`,
      vals: [...charts.cpa.vals.slice(0, -1), Number(cpa.toFixed(2))],
      threshold: target * overFactor,
      thr_label: `目标 × ${overFactor.toFixed(1)} = ${money(target * overFactor)}`,
      base: target,
      base_label: `目标 ${money(target, 0)}`,
    },
    freq: charts.freq,
  }
}

function liveFindings(campaigns: Campaign[], w5: W5State) {
  const mug = campaigns.find((campaign) => campaign.id === 'c-mug-us')
  const lamp = campaigns.find((campaign) => campaign.id === 'c-lamp-au')
  const mugCpa = mug && mug.orders > 0 ? mug.spendMonth / mug.orders : 0
  const target = cpaTargets['c-mug-us'] ?? 38
  const over = Math.round((mugCpa / target - 1) * 100)
  return report.findings.map((finding, index) => {
    const hand = w5.findings[finding.id]
    const resolved = finding.id === 'f-lamp-link' && lamp?.attentionReason !== 'product_link_invalid'
    const handed = hand?.owner === 'ai' && hand.handedAt !== null
    const evidence = report.evidence[index]
    return {
      ...finding,
      title: finding.id === 'f-mug-cpa' ? `轻量保温杯每单成本高于目标 ${over}%` : finding.title,
      evidence: finding.id === 'f-mug-cpa'
        ? { ...evidence, value: money(mugCpa), sub: `目标 ${money(target, 0)} · 连续 7 天` }
        : evidence,
      owner: resolved ? 'resolved' as const : handed ? 'ai' as const : finding.owner,
      state: resolved ? '已解决' : handed ? 'AI 已接手' : finding.state,
      todo: handed ? finding.handoff.willDo : finding.todo,
      handedAt: handed ? hand.handedAt : null,
    }
  })
}

function liveAutoRules(w5: W5State): Array<AutoRuleSeed | W5CustomRule> {
  return [
    ...rules.items.map((rule) => ({
      ...rule,
      on: rule.locked ? true : w5.autoRules.find((item) => item.key === rule.key)?.on ?? rule.on,
    })),
    ...w5.customRules,
  ]
}

function benchmarkRows(account: LiveAccount) {
  const you = new Map<string, string>([
    ['点击率', `${(account.ctr * 100).toFixed(2)}%`],
    ['千次触达成本', money(account.cpm)],
    ['每单成本', money(account.cpa)],
    ['广告回报', account.roas.toFixed(1)],
    ['频次（近 7 天）', account.frequency.toFixed(1)],
  ])
  return benchmark.rows.map((row) => ({ ...row, you: you.get(row.metric) ?? row.you }))
}

function percentChange(values: number[]): number {
  const first = values[0] ?? 0
  const last = values.at(-1) ?? first
  return first === 0 ? 0 : Math.round((last / first - 1) * 100)
}

export function selectW5Live(campaigns: Campaign[], ads: Ad[], w5: W5State) {
  const liveW5 = normalizeW5(w5)
  const account = selectAccount(campaigns, ads)
  const alerts = selectAlerts(campaigns, ads, liveW5)
  const findings = liveFindings(campaigns, liveW5)
  const autoRules = liveAutoRules(liveW5)
  const mug = campaigns.find((campaign) => campaign.id === 'c-mug-us')
  const fromDailyMax = liveW5.proposal.fromDailyMax ?? mug?.budget.dailyMax ?? 0
  const proposedDailyMax = liveW5.proposal.dailyMax ?? Math.round(fromDailyMax * 1.2)
  const avgDaily = mug ? mug.spendMonth / 7 : 0
  const spentPercent = mug ? Math.round(mug.spendMonth / mug.budget.total * 100) : 0
  const dailyUsed = fromDailyMax > 0 ? Math.round(avgDaily / fromDailyMax * 100) : 0
  const remaining = mug ? Math.max(0, mug.budget.total - mug.spendMonth) : 0
  const outDate = dateAfter(avgDaily > 0 ? Math.ceil(remaining / avgDaily) : 0)
  const formatBudget = (value: number) => formatUsd(value, 0)
  const pendingVars = {
    from: formatBudget(fromDailyMax),
    to: formatBudget(proposedDailyMax),
    roas: (mug?.metrics.roas ?? 0).toFixed(1),
    avgDaily: formatBudget(avgDaily),
    dailyMax: formatBudget(fromDailyMax),
    dailyUsed: `${dailyUsed}%`,
    monthUsed: `${spentPercent}%`,
    outDate,
  }
  const pendingReasons = rules.pending.why.map((reason) => fillTemplate(reason, pendingVars))
  const aiHandled = findings.filter((finding) => finding.owner === 'ai').length
  const needYou = findings.filter((finding) => finding.owner === 'you').length
  const actions = [...report.actions]
  report.findings.forEach((finding) => {
    const hand = liveW5.findings[finding.id]
    if (hand?.owner === 'ai' && hand.handedAt !== null) {
      actions.push({ owner: 'ai', text: finding.handoff.willDo, when: '明天 09:00 检查时执行' })
    }
  })
  const triggerLog = liveW5.triggerLog.map((entry) => {
    if (entry.id !== 't-1' && !entry.byUser) return entry
    const restored = entry.what.match(/\$[\d,]+/)?.[0] ?? formatBudget(fromDailyMax)
    const result = entry.state === 'pending' ? '等待你确认'
      : entry.state === 'done' ? `已执行，日预算上限已更新为 ${formatBudget(liveW5.proposal.dailyMax ?? proposedDailyMax)}`
        : entry.state === 'rejected' ? `你已拒绝，日预算上限保持 ${restored}`
          : entry.state === 'undone' ? `你于 ${entry.undoneAt ?? '—'} 撤销，日预算上限已恢复为 ${restored}`
            : entry.result
    return {
      ...entry,
      what: entry.id === 't-1' ? fillTemplate(entry.what, pendingVars) : entry.what,
      result,
    }
  }).map((entry, index) => ({ entry, index })).sort((left, right) => {
    const leftTime = parseStationTime(left.entry.at)
    const rightTime = parseStationTime(right.entry.at)
    for (let index = 0; index < leftTime.length; index += 1) {
      const difference = (rightTime[index] ?? 0) - (leftTime[index] ?? 0)
      if (difference !== 0) return difference
    }
    return left.index - right.index
  }).map(({ entry }) => entry)
  return {
    account,
    syncLabel: `最近同步 ${fixture.meta.syncedAt} · 近 7 天`,
    metricItems: metrics.items.map((item) => ({ ...item, value: metricValue(item.key, account), note: metricNote(item, campaigns) })),
    monitorRules: metrics.rules.map((rule) => ({ ...rule, on: liveW5.monitorRules[rule.key] !== false, hits: alerts.filter((alert) => alert.ruleKey === rule.key && alert.tone !== 'ok').length })),
    alerts,
    alertCount: alerts.filter((alert) => alert.tone !== 'ok').length,
    taskRows: selectTaskRows(campaigns, ads, w5),
    charts: liveCharts(campaigns),
    report: {
      ...report,
      stats: [
        { value: String(findings.length), label: '项发现' },
        { value: String(aiHandled), label: '项 AI 已处理' },
        { value: String(needYou), label: '项需要你', tone: 'you' },
        { value: String(liveW5.triggerLog.filter((entry) => entry.state === 'done').length), label: '次自动调整（本周）' },
      ],
      kpis: report.kpis.map((kpi) => ({
        ...kpi,
        value: kpi.label === '花费' ? formatUsd(account.spend, 0)
          : kpi.label === '转化订单' ? formatInteger(account.orders)
            : kpi.label === '销售额' ? formatUsd(account.revenue, 0)
              : kpi.label === '广告回报' ? account.roas.toFixed(1)
                : kpi.label === '每单成本' ? money(account.cpa)
                  : `${(account.ctr * 100).toFixed(2)}%`,
      })),
      findings,
      actions,
      reportSentAt: liveW5.reportSentAt,
      reportExports: liveW5.reportExports,
      reportSendCount: liveW5.reportSendCount,
    },
    automation: {
      rules: autoRules,
      groupOrder: rules.groupOrder,
      guards: rules.guards,
      enabledRules: autoRules.filter((rule) => rule.locked || rule.on).length,
      triggerLog,
      weekRuns: liveW5.triggerLog.length,
      skippedRuns: liveW5.triggerLog.filter((entry) => entry.state === 'skip').length,
      pending: {
        ...rules.pending,
        obj: mug?.adName ?? rules.pending.obj,
        what: fillTemplate(rules.pending.what, pendingVars),
        why: pendingReasons,
        from: fromDailyMax,
        to: proposedDailyMax,
      },
      proposal: liveW5.proposal,
      logState: rules.logState,
      benchmark: { ...benchmark, rows: benchmarkRows(account) },
      marketChange: percentChange(benchmark.trend.mkt),
      yourChange: percentChange(benchmark.trend.you),
    },
  }
}

export type W5Live = ReturnType<typeof selectW5Live>
export type LiveTriggerEntry = W5TriggerEntry

export function useW5Live(): W5Live {
  const campaigns = useDemoStore((state) => state.campaigns)
  const ads = useDemoStore((state) => state.ads)
  const w5 = useDemoStore((state) => state.w5)
  return useMemo(() => selectW5Live(campaigns, ads, w5), [ads, campaigns, w5])
}

export function assertW5Mirrors(): void {
  const issues: string[] = []
  const taskSeeds = (tasksJson as { items: Array<{ campaignId: string; adName: string; product: string }> }).items
  const taskMismatch = taskSeeds.some((seed) => {
    const campaign = fixture.campaigns.find((item) => item.id === seed.campaignId)
    return !campaign || campaign.adName !== seed.adName || productsById.get(campaign.productId)?.name !== seed.product
  })
  const account = selectAccount(fixture.campaigns, adsJsonForAssertion())
  if (taskMismatch) issues.push('任务镜像')
  if (metrics.items.some((item) => item.value !== metricValue(item.key, account))) issues.push('指标镜像')
  const reportExpected = new Map<string, string>([
    ['花费', formatUsd(account.spend, 0)],
    ['转化订单', formatInteger(account.orders)],
    ['销售额', formatUsd(account.revenue, 0)],
    ['广告回报', account.roas.toFixed(1)],
    ['每单成本', money(account.cpa)],
    ['点击率', `${(account.ctr * 100).toFixed(2)}%`],
  ])
  if (report.kpis.some((item) => item.value !== reportExpected.get(item.label))) issues.push('报告镜像')

  const currentTime = parseStationTime(stationNow())
  const [, todayMonth = '0', todayDay = '0'] = fixture.meta.today.split('-')
  if (currentTime[0] !== Number(todayMonth) || currentTime[1] !== Number(todayDay) || currentTime[2] > 23 || currentTime[3] > 59) issues.push('站内时钟')
  if (rules.log.some((entry) => {
    const time = parseStationTime(entry.at)
    return time.some((value, index) => value > (currentTime[index] ?? 0) && time.slice(0, index).every((part, prior) => part === currentTime[prior]))
  })) issues.push('未来触发记录')

  const triggerKeys = rules.log.map((entry) => [entry.rule, entry.object, entry.what, entry.hit ?? ''].join('|'))
  if (new Set(triggerKeys).size !== triggerKeys.length) issues.push('重复触发记录')
  if (new Set(rules.log.map((entry) => entry.hit)).size !== rules.log.length) issues.push('重复命中值')
  if (new Set(report.findings.map((finding) => finding.handoff.willDo)).size !== report.findings.length) issues.push('重复交接文案')

  const process = copyJson.process
  const processGroups = Object.entries(process).filter(([, value]) => Array.isArray(value)) as Array<[string, Array<{ label: string; detail: string }>]>
  if (process.stepMs > 300 || process.holdMs < 400 || processGroups.some(([, steps]) => steps.length > 4 || steps.some((step) => !step.label || !step.detail))) issues.push('过程模板')

  const mug = fixture.campaigns.find((campaign) => campaign.id === 'c-mug-us')
  const pendingFrom = mug ? formatUsd(mug.budget.dailyMax, 0) : ''
  if (!fillTemplate(rules.pending.what, { from: pendingFrom, to: pendingFrom }).includes(pendingFrom)) issues.push('预算权威')

  if (issues.length > 0) console.error(`w5 装载期断言失败：${issues.join('、')}`)
}

function adsJsonForAssertion(): Ad[] {
  return useDemoStore.getInitialState().ads
}

if (import.meta.env.DEV) assertW5Mirrors()
